import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { NotFoundPage } from './NotFoundPage';
import {
  getCandidate as getReportV2Candidate,
  getCandidates as getReportV2Candidates,
} from '@/modules/playground-v2/mockData';
import {
  getCandidate as getReportV3Candidate,
  getCandidates as getReportV3Candidates,
} from '@/modules/playground-v3/mockData';

function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center w-full h-full min-h-[60vh]">
      <Loader2 size={24} strokeWidth={1.75} className="animate-spin" style={{ color: 'var(--accent)' }} />
    </div>
  );
}

// Modal-shaped Suspense fallback used by the plan/edit routes. Matches the
// PlanModeView overlay so navigating into /plan or /edit doesn't briefly
// expose the AppShell content area before the modal mounts.
function PlanEditLoadingFallback() {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-stretch justify-stretch px-4 pt-4 md:px-20 md:pt-20 pb-20 md:pb-20"
      style={{ background: 'var(--surface-overlay)', fontFamily: 'var(--v2-font-sans)' }}
    >
      <div
        className="flex flex-col rounded-2xl overflow-hidden flex-1 items-center justify-center"
        style={{ background: 'var(--surface-primary)', boxShadow: 'var(--shadow-xl)' }}
      >
        <Loader2 size={24} strokeWidth={1.75} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    </div>
  );
}

function withSuspense(element: ReactElement) {
  return <Suspense fallback={<PageLoadingFallback />}>{element}</Suspense>;
}

function withPlanEditSuspense(element: ReactElement) {
  return <Suspense fallback={<PlanEditLoadingFallback />}>{element}</Suspense>;
}

function lazyPage(loader: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(loader);
  return withSuspense(<Page />);
}

function lazyPlanEditPage(loader: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(loader);
  return withPlanEditSuspense(<Page />);
}

const JobEditLayout = lazy(() => import('@/app/v2/jobs/[id]/edit/layout'));
const JobEditRootPage = lazy(() => import('@/app/v2/jobs/[id]/edit/page'));
const JobEditSubPage = lazy(() => import('@/app/v2/jobs/[id]/edit/[subpage]/page'));

function JobEditRootRoute() {
  return withPlanEditSuspense(
    <JobEditLayout>
      <JobEditRootPage />
    </JobEditLayout>,
  );
}

function JobEditSubPageRoute() {
  return withSuspense(
    <JobEditLayout>
      <JobEditSubPage />
    </JobEditLayout>,
  );
}

function ReportV2LandingRoute() {
  const first = getReportV2Candidates()[0];
  return <Navigate to={`/v2/playground/report-v2/${first.id}`} replace />;
}

function ReportV2DetailRoute() {
  const { id } = useParams();
  const candidate = id ? getReportV2Candidate(id) : null;

  if (!candidate) return <NotFoundPage />;
  return <CandidateReportV2 candidate={candidate} />;
}

function ReportV3LandingRoute() {
  const first = getReportV3Candidates()[0];
  return <Navigate to={`/v2/playground/report-v3/${first.id}`} replace />;
}

function ReportV3DetailRoute() {
  const { id } = useParams();
  const candidate = id ? getReportV3Candidate(id) : null;

  if (!candidate) return <NotFoundPage />;
  return <CandidateReportV3 candidate={candidate} />;
}

const CandidateReportV2 = lazy(() =>
  import('@/modules/playground-v2/report/CandidateReportV2').then((module) => ({ default: module.CandidateReportV2 })),
);
const CandidateReportV3 = lazy(() =>
  import('@/modules/playground-v3/report/CandidateReportV3').then((module) => ({ default: module.CandidateReportV3 })),
);

export const v2RouteElements = {
  'agent-dashboard': lazyPage(() => import('@/app/v2/agent-dashboard/page')),
  calendar: lazyPage(() => import('@/app/v2/calendar/page')),
  'create-org': lazyPage(() => import('@/app/v2/create-org/page')),
  'cv-screening-create': lazyPage(() => import('@/app/v2/cv-screening/create/page')),
  'cv-screening-edit': lazyPage(() => import('@/app/v2/cv-screening/[id]/edit/page')),
  'ai-interview-create': lazyPage(() => import('@/app/v2/ai-interview/create/page')),
  'ai-interview-edit': lazyPage(() => import('@/app/v2/ai-interview/[id]/edit/page')),
  'design-system': lazyPage(() => import('@/app/v2/design-system/page')),
  jobs: lazyPage(() => import('@/app/v2/jobs/page')),
  'jobs-v2': lazyPage(() => import('@/app/v2/jobs-v2/page')),
  'job-detail': lazyPage(() => import('@/app/v2/jobs/[id]/page')),
  'job-edit-root': <JobEditRootRoute />,
  'job-edit-subpage': <JobEditSubPageRoute />,
  'job-icp-builder': lazyPage(() => import('@/app/v2/jobs/[id]/icp-builder/page')),
  'job-outreach': lazyPage(() => import('@/app/v2/jobs/[id]/outreach/page')),
  'job-campaign-sequence': lazyPage(() => import('@/app/v2/jobs/[id]/campaigns/[campaignId]/sequence/page')),
  'job-plan': lazyPlanEditPage(() => import('@/app/v2/jobs/[id]/plan/page')),
  'job-quick-setup': lazyPage(() => import('@/app/v2/jobs/[id]/quick-setup/page')),
  'new-job': lazyPage(() => import('@/app/v2/jobs/new/page')),
  // Phase 1 — single-turn job creation via the job_create agent.
  'new-job-agentic': lazyPage(() => import('@/app/v2/jobs/new/agentic/page')),
  'new-role-quick-setup': lazyPage(() => import('@/app/v2/jobs/new-role/quick-setup/page')),
  'new-role-icp-builder': lazyPage(() => import('@/app/v2/jobs/new-role/icp-builder/page')),
  // The authed agentic flow is now the wired-up /v2/chats surface.
  // The old static demo lives at /demo/agentic for the YC pitch.
  'new-role-agentic': <Navigate to="/v2/chats" replace />,
  'chat-detail': lazyPage(() => import('@/app/v2/chats/[id]/page')),
  // Claude Code-shaped harness, Stage 1 — pure chat, no tools.
  'agent-chat-detail': lazyPage(() => import('@/app/agent/[id]/page')),
  inbox: lazyPage(() => import('@/app/v2/inbox/page')),
  'outreach-campaigns': lazyPage(() => import('@/app/v2/outreach/campaigns/page')),
  'campaign-detail': lazyPage(() => import('@/app/v2/outreach/campaigns/[campaignId]/page')),
  'campaign-sequence': lazyPage(() => import('@/app/v2/outreach/campaigns/[campaignId]/sequence/page')),
  'outreach-analytics': lazyPage(() => import('@/app/v2/outreach/analytics/page')),
  'outreach-settings': lazyPage(() => import('@/app/v2/settings/outreach/page')),
  // search-v2 — same detail experience, different agentType (the page
  // is agentType-agnostic; surface.ts resolves links from the route).
  'search-v2-detail': lazyPage(() => import('@/app/agent/[id]/page')),
  // search-v3 — same agentType-agnostic detail page; surface.ts maps the
  // /search-v3 paths to the search_v3 agentType.
  'search-v3-detail': lazyPage(() => import('@/app/agent/[id]/page')),
  people: lazyPage(() => import('@/app/v2/people/page')),
  'custom-fields': lazyPage(() => import('@/app/v2/people/custom-fields/page')),
  'permission-test': lazyPage(() => import('@/app/v2/permission-test/page')),
  'playground-report': lazyPage(() => import('@/app/v2/playground/candidate-report/page')),
  'playground-kanban': lazyPage(() => import('@/app/v2/playground/kanban/page')),
  'playground-kanban-v2': lazyPage(() => import('@/app/v2/playground/kanban-v2/page')),
  'playground-kanban-v3': lazyPage(() => import('@/app/v2/playground/kanban-v3/page')),
  'playground-report-v2': <ReportV2LandingRoute />,
  'playground-report-v2-detail': withSuspense(<ReportV2DetailRoute />),
  'playground-report-v3': <ReportV3LandingRoute />,
  'playground-report-v3-detail': withSuspense(<ReportV3DetailRoute />),
  integrations: lazyPage(() => import('@/app/v2/settings/integrations/page')),
  'integration-accounts': lazyPage(() => import('@/app/v2/settings/integrations/accounts/page')),
  invite: lazyPage(() => import('@/app/v2/settings/invite/page')),
  members: lazyPage(() => import('@/app/v2/settings/members/page')),
  roles: lazyPage(() => import('@/app/v2/settings/roles/page')),
  'tmp-kanban': lazyPage(() => import('@/app/v2/tmp-kanban/[assessmentId]/page')),
} satisfies Record<string, ReactElement>;
